<template>
    <div class="item" :class="{ 'has-discount': item.hasDiscount, 'has-image': hasImage, disabled: !isSelectable }" @click="onClick">
        <div v-if="hasImage" class="image">
            <img :src="model.imagePath" alt="" />
        </div>

        <div class="head">
            <QuantitySelectionCheckbox
                v-if="isSelectable"
                :selectedQuantity="isSelected ? 1 : 0"
                :totalQuantity="1"
                :visible="true"
            />

            <div class="quantity">{{ model.quantity }}x</div>
            <div class="name">{{ getNameForCurrentUser(model) }}<ItemIcons :articleId="model.articleId" /></div>
            <div class="increaser" :class="{ hidden: !canChangeQuantity }" @click.stop>
                <DecreaseBtn :disabled="!isDecreaseAvailable" @click="onDecreaseClick" />
                <IncreaseBtn :disabled="!isIncreaseAvailable" @click="onIncreaseClick" />
            </div>
            <CartItemPrice class="price" :item="item" @click="onPriceClick" />
            <RemoveItemBtn :disabled="!isRemoveAvailable" @click.stop="onRemoveItemClick" />
        </div>

        <div class="subs" v-if="item.options.length">
            <CartItemOptionGroup
                v-for="optionGroup in item.options"
                :key="optionGroup.id"
                :optionGroup="optionGroup"
                :multiplier="item.quantity"
            />
        </div>

        <CartItemCommentEditor
            @click.stop
            :item="item"
            :disabled="props.disabled"
            :isOperatorUser="identity.hasOperatorAccess"
            :areItemCommentsAllowed="cart.areItemCommentsAllowed"
        />
    </div>
</template>

<script setup lang="ts">
import type { ICartItem } from "@/data/model/Customer";
import { useCartStore } from "@/data/stores/cart";
import { useIdentityStore } from "@/data/stores/identity";
import { useOfferStore } from "@/data/stores/offer";
import { getNameForCurrentUser } from "@/shared/helpers/ItemNameHelper";
import { computed } from "vue";



import CartItemOptionGroup from "@/app/components/cart/CartItemOptionGroup.vue";
import DecreaseBtn from "@/app/components/cart/DecreaseBtn.vue";
import IncreaseBtn from "@/app/components/cart/IncreaseBtn.vue";
import RemoveItemBtn from "@/app/components/cart/RemoveItemBtn.vue";
import CartItemCommentEditor from "@/components/cart/CartItemCommentEditor.vue";
import CartItemPrice from "@/components/cart/CartItemPrice.vue";
import QuantitySelectionCheckbox from "@/components/elements/QuantitySelectionCheckbox.vue";
import ItemIcons from "@/components/order/ItemIcons.vue";


const cart = useCartStore();
    const identity = useIdentityStore();
    const offer = useOfferStore();
    const isSelected = computed(() => cart.isCartItemSelected(props.item));

    const props = defineProps<{
        item: ICartItem;
        disabled?: boolean;
    }>();

    const emit = defineEmits<{
        decrease: [cartItem: ICartItem];
        increase: [cartItem: ICartItem];
        remove: [cartItem: ICartItem];
    }>();

    const model = computed(() => props.item);

    // the Mobile POS is the smaller screen and the cart matters less to an operator, so it follows the
    // operator setting for item images, like the add to cart popover
    const hasImage = computed(
        () =>
            model.value.imagePath != null &&
            model.value.imagePath.length > 0 &&
            (!identity.hasOperatorAccess || offer.orderService?.useCase?.operatorDisplayItemImages === true)
    );
    // selecting cart items only feeds the discount button, so it is pointless without discounts
    const isSelectable = computed(() => !props.disabled && offer.areDiscountsEnabled && identity.hasOperatorAccess);
    const cartItemMinQuantity = computed(() => model.value.minQuantity ?? 1);
    const cartItemMaxQuantity = computed(() => {
        const maxTotalPerArticle = model.value.maxQuantity ?? Number.MAX_VALUE;
        const maxTotalUnitsPerOrder = cart.orderService?.useCase?.maximumTotalUnitsPerOrder ?? Number.MAX_VALUE;
        const remainingUnitsInOrder = maxTotalUnitsPerOrder - (cart.totalCartMainItems - model.value.quantity);
        return Math.min(maxTotalPerArticle, remainingUnitsInOrder);
    });

    const isDecreaseAvailable = computed(() => model.value.quantity > cartItemMinQuantity.value);
    const isIncreaseAvailable = computed(() => model.value.quantity < cartItemMaxQuantity.value);
    const isRemoveAvailable = computed(() => true);

    const canChangeQuantity = computed(() => cartItemMinQuantity.value !== cartItemMaxQuantity.value);

    async function onDecreaseClick() {
        if (!isDecreaseAvailable.value) return;
        emit("decrease", model.value);
    }

    async function onIncreaseClick() {
        if (!isIncreaseAvailable.value) return;
        emit("increase", model.value);
    }

    async function onRemoveItemClick() {
        if (!isRemoveAvailable.value) return;
        emit("remove", model.value);
    }

    async function onPriceClick(event: MouseEvent) {
        if (!model.value.hasDiscount) return;

        // without a discount to remove the click falls through to the row and changes the selection
        event.stopPropagation();
        cart.showConfirmItemDiscountsRemovalPopup(model.value);
    }

    function onClick() {
        if (!isSelectable.value) return;

        cart.toggleCartItemSelection(props.item);
    }
</script>
